import { useState } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Sparkles, BookOpen, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OnboardingScreen({ onComplete }) {
  const { user, setUserData } = useAuth();
  const [loading, setLoading] = useState(false);

  const selectMode = async (mode) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/user/settings`, { mode, onboarded: true });
      if (res.data.success) {
        setUserData(res.data.user);
        onComplete();
      }
    } catch {
      onComplete(); // proceed anyway
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-app-screen px-4 py-8" data-testid="onboarding">
      <Sparkles className="h-10 w-10 text-amber-400 mb-4" />
      <h1 className="font-cinzel text-2xl sm:text-3xl font-bold text-amber-400 tracking-wider mb-2 text-center">Welcome, {user?.name?.split(" ")[0] || "there"}!</h1>
      <p className="text-slate-400 text-sm mb-10 text-center max-w-md">Choose how you'd like your readings delivered</p>

      <div className="grid gap-4 sm:grid-cols-2 max-w-xl w-full">
        <button onClick={() => selectMode("simple")} disabled={loading}
          className="glass rounded-2xl p-6 text-left hover:bg-white/5 transition-all duration-300 group border border-amber-500/20"
          data-testid="onboard-simple">
          <Zap className="h-8 w-8 text-amber-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-cinzel text-amber-400 font-bold text-sm tracking-wide mb-1">Simple Mode</h3>
          <p className="text-xs text-slate-400 leading-relaxed mb-2">Clear, concise answers in plain English. Perfect for quick guidance and daily readings.</p>
          <ul className="space-y-1">
            <li className="text-[11px] text-slate-500">Short answers (under 200 words)</li>
            <li className="text-[11px] text-slate-500">Yes/No with brief explanation</li>
            <li className="text-[11px] text-slate-500">Easy to understand, no jargon</li>
          </ul>
        </button>
        <button onClick={() => selectMode("astrologer")} disabled={loading}
          className="glass rounded-2xl p-6 text-left hover:bg-white/5 transition-all duration-300 group border border-violet-500/20"
          data-testid="onboard-astrologer">
          <BookOpen className="h-8 w-8 text-violet-400 mb-3 group-hover:scale-110 transition-transform" />
          <h3 className="font-cinzel text-violet-400 font-bold text-sm tracking-wide mb-1">Astrologer Mode</h3>
          <p className="text-xs text-slate-400 leading-relaxed mb-2">In-depth technical analysis with full planetary references. For astrologers and serious students.</p>
          <ul className="space-y-1">
            <li className="text-[11px] text-slate-500">Detailed D-1 & D-9 analysis</li>
            <li className="text-[11px] text-slate-500">Specific Dasha timelines with dates</li>
            <li className="text-[11px] text-slate-500">Technical terminology included</li>
          </ul>
        </button>
      </div>

      <p className="text-[11px] text-slate-600 mt-6 text-center">You can change this anytime in Profile settings</p>
    </div>
  );
}
